import React from "react";
import { Link } from "react-router-dom";
import Spinner from "./Spinner";

export type ButtonVariant =
  | "primary"
  | "accent"
  | "ghost"
  | "outline"
  | "danger"
  | "subtle";
export type ButtonSize = "sm" | "md" | "lg";

interface CommonProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  fullWidth?: boolean;
  className?: string;
  children?: React.ReactNode;
}

type AsButton = CommonProps &
  Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, keyof CommonProps> & {
    to?: undefined;
    href?: undefined;
  };
type AsLink = CommonProps &
  Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, keyof CommonProps> & {
    to: string;
    href?: undefined;
  };
type AsAnchor = CommonProps &
  Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, keyof CommonProps> & {
    href: string;
    to?: undefined;
  };

export type ButtonProps = AsButton | AsLink | AsAnchor;

const base =
  "inline-flex items-center justify-center gap-2 font-medium rounded-full " +
  "transition-all duration-200 ease-out select-none whitespace-nowrap " +
  "focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 " +
  "focus-visible:ring-offset-gray-900 disabled:opacity-50 " +
  "disabled:cursor-not-allowed disabled:hover:translate-y-0";

const variants: Record<ButtonVariant, string> = {
  primary:
    "bg-blue-600 text-white hover:bg-blue-500 hover:-translate-y-0.5 " +
    "hover:shadow-glow focus-visible:ring-blue-400 active:translate-y-0",
  accent:
    "bg-accent-500 text-gray-950 font-semibold hover:bg-accent-400 " +
    "hover:-translate-y-0.5 hover:shadow-glow-accent focus-visible:ring-accent-300 " +
    "active:translate-y-0",
  outline:
    "border border-gray-600 text-gray-100 hover:border-blue-400 " +
    "hover:text-blue-300 hover:-translate-y-0.5 focus-visible:ring-blue-400",
  ghost:
    "text-gray-300 hover:text-white hover:bg-gray-700/60 focus-visible:ring-gray-500",
  subtle:
    "bg-gray-800 text-gray-100 border border-gray-700 hover:bg-gray-700 " +
    "hover:border-gray-600 focus-visible:ring-gray-500",
  danger:
    "bg-red-600 text-white hover:bg-red-500 hover:-translate-y-0.5 " +
    "focus-visible:ring-red-400 active:translate-y-0",
};

const sizes: Record<ButtonSize, string> = {
  sm: "text-sm px-4 py-1.5",
  md: "text-sm px-6 py-2.5",
  lg: "text-base px-8 py-3",
};

function classesFor(
  variant: ButtonVariant,
  size: ButtonSize,
  fullWidth: boolean,
  extra: string
) {
  return `${base} ${variants[variant]} ${sizes[size]} ${
    fullWidth ? "w-full" : ""
  } ${extra}`.trim();
}

/**
 * The single Button primitive. Renders a react-router <Link> when `to` is set,
 * an <a> when `href` is set, otherwise a <button>. Replaces the ~50 inline
 * `bg-blue-600 …` button strings scattered through the app.
 */
const Button: React.FC<ButtonProps> = (props) => {
  const {
    variant = "primary",
    size = "md",
    loading = false,
    leftIcon,
    rightIcon,
    fullWidth = false,
    className = "",
    children,
    to,
    href,
    ...rest
  } = props as CommonProps & { to?: string; href?: string } & Record<
      string,
      unknown
    >;

  const cls = classesFor(variant, size, fullWidth, className);
  const content = (
    <>
      {loading ? (
        <Spinner size="sm" tone="current" />
      ) : (
        leftIcon && <span className="shrink-0">{leftIcon}</span>
      )}
      {children}
      {!loading && rightIcon && <span className="shrink-0">{rightIcon}</span>}
    </>
  );

  if (to !== undefined) {
    return (
      <Link to={to} className={cls} {...(rest as any)}>
        {content}
      </Link>
    );
  }

  if (href !== undefined) {
    return (
      <a href={href} className={cls} {...(rest as any)}>
        {content}
      </a>
    );
  }

  return (
    <button
      className={cls}
      disabled={loading || (rest as any).disabled}
      {...(rest as React.ButtonHTMLAttributes<HTMLButtonElement>)}
    >
      {content}
    </button>
  );
};

export default Button;
